import {
  Box,
  FormLabel,
  Grid,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { PermissionCheck } from "../../App/PermissionCheck";
import BoxHeader from "../../Components/Box/BoxHeader";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import { useCallback, useEffect, useState } from "react";
import {
  GetApiMethod,
  PostApiMethod,
  PutApiMethod,
  DeleteApiMethod,
} from "../../Utils/ApiService";
import { CustomModal, ModalStyle } from "../../Components/ModalStyle";
import { sportSchema } from "../HomePages/AddUserSchema";
import { Form, Formik } from "formik";
import { showLoader } from "../../Store/loaderSlice";
import {
  DisplayErrorMessages,
  makeQueryParameters,
} from "../../Utils/HelperService";
import { showToaster } from "../../Store/toastSlice";
import { useAppDispatch } from "../../Store/hooks";
import MenuGroup from "../../Components/DropDown/MenuGroup";
import { useNavigate } from "react-router-dom";
import { CustomTablePagination } from "../../Components/Pagination/CustomTablePagination";

interface SportsDto {
  name: string;
  id: string;
  isSports: boolean;
  status: boolean;
}

export interface SportsListDto {
  name: string;
  id: string;
  isSports: boolean;
  parentId: string | null;
  subParentId?: string | null;
  status: boolean;
}

const Sports = () => {
  const [search, setSearch] = useState("");
  const [sportsList, setSportsList] = useState<{
    data: SportsListDto[];
    count: number;
  }>({ data: [], count: 0 });
  const [formModal, setFormModal] = useState(false);
  const initialValue: SportsDto = {
    name: "",
    id: "",
    isSports: true,
    status: true,
  };
  const [sportsData, setSportsData] = useState<SportsDto>(initialValue);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState({ pageNo: 1, pageSize: 5 });
  const getSports = useCallback(
    (search: string = "", page = 1, limit = 5) => {
      dispatch(showLoader({ showLoader: true }));
      const param = {
        search: search,
        sortDirection: "DESC",
        page: page,
        limit: limit,
        sort: "createdAt",
      };
      GetApiMethod("/sports?" + makeQueryParameters(param))
        .then((res) => {
          const { count, rows } = res.data.data;
          setSportsList({ count: count, data: rows });
          dispatch(showLoader({ showLoader: false }));
        })
        .catch((error) => {
          dispatch(showLoader({ showLoader: false }));
          setSportsList({ data: [], count: 0 });
        });
    },
    [dispatch]
  );

  useEffect(() => {
    getSports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createSports = useCallback(
    async (data: SportsDto) => {
      try {
        dispatch(showLoader({ showLoader: true }));
        const apiMethod = data.id
          ? PutApiMethod("sports/" + data.id, data)
          : PostApiMethod("sports", data);
        const res = await apiMethod;
        dispatch(showLoader({ showLoader: false }));
        getSports();
        setFormModal(false);
        dispatch(
          showToaster({
            dialogBgColor: "success",
            dialogMsg: res.data.message,
            showDialog: true,
            timer: "5000",
          })
        );
      } catch (error) {
        dispatch(showLoader({ showLoader: false }));
        const errorMsg = DisplayErrorMessages(error);

        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: errorMsg,
            showDialog: true,
            timer: "5000",
          })
        );
      }
    },
    [dispatch, getSports]
  );

  const deleteSports = useCallback(
    async (id: string) => {
      try {
        const confirmed = window.confirm(
          "Are you sure you want to delete this sport?"
        );
        if (!confirmed) return;

        dispatch(showLoader({ showLoader: true }));
        const res = await DeleteApiMethod("sports/" + id);
        dispatch(showLoader({ showLoader: false }));
        getSports(search, page.pageNo, page.pageSize);
        dispatch(
          showToaster({
            dialogBgColor: "success",
            dialogMsg: res.data.message || "Sport deleted successfully",
            showDialog: true,
            timer: "5000",
          })
        );
      } catch (error) {
        dispatch(showLoader({ showLoader: false }));
        const errorMsg = DisplayErrorMessages(error);
        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: errorMsg,
            showDialog: true,
            timer: "5000",
          })
        );
      }
    },
    [dispatch, getSports, page.pageNo, page.pageSize, search]
  );

  return (
    <>
      <AuthorizedLayout>
        <BoxHeader
          dashboard_name="Sports Management"
          button_name={"Add Sports"}
          onAdd={() => {
            setFormModal(true);
            setSportsData({
              id: "",
              name: "",
              isSports: true,
              status: true,
            });
          }}
          button_show={PermissionCheck("manage_sports")}
        />
        <Box className="mt-4 mb-3">
          <FormLabel
            className="fw-600"
            sx={{ display: "block", marginBottom: "8px" }}
          >
            Search :
          </FormLabel>
          <Box sx={{ position: "relative", width: 400 }}>
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#999",
                pointerEvents: "none",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </Box>
            <TextField
              placeholder="Search Sports"
              variant="standard"
              size="small"
              value={search}
              onChange={(e) => {
                getSports(e.target.value, 1, page.pageSize);
                setSearch(e.target.value);
              }}
              sx={{
                width: "100%",
                "& .MuiInput-root": {
                  paddingLeft: "28px",
                  "&:before": {
                    borderBottom: "1px solid #d3d3d3",
                  },
                  "&:hover:not(.Mui-disabled):before": {
                    borderBottom: "1px solid #999",
                  },
                  "&:after": {
                    borderBottom: "2px solid #09BBE9",
                  },
                },
              }}
            />
          </Box>
          <TableContainer className="mt-2">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>

                  <TableCell>Detail</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sportsList.data.map((val, key) => (
                  <TableRow key={key}>
                    <TableCell>{val.name}</TableCell>
                    <TableCell
                      sx={{
                        textDecoration: "underline",
                        color: "blue",
                        cursor: "pointer",
                      }}
                      onClick={() => navigate(`/sports/${val.id}`)}
                    >
                      View
                    </TableCell>

                    <TableCell align="right">
                      <MenuGroup
                        id={val.id}
                        view={false}
                        edit={true}
                        delete={true}
                        clickEdit={() => {
                          setFormModal(true);
                          setSportsData({
                            id: val.id,
                            name: val.name,
                            isSports: true,
                            status: val.status,
                          });
                        }}
                        clickDelete={() => deleteSports(val.id)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {sportsList.data.length === 0 && (
                  <TableRow>
                    <TableCell align="center">No Data</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={sportsList.count}
            page={page.pageNo - 1}
            rowsPerPage={page.pageSize}
            onPageChange={(_, newPage) => {
              getSports(search, newPage + 1, page.pageSize);

              setPage({ ...page, pageNo: newPage });
            }}
            onRowsPerPageChange={(event) => {
              console.log(event.target.value);
              getSports(search, page.pageNo, parseInt(event.target.value, 10));
              setPage({ ...page, pageSize: parseInt(event.target.value, 10) });
            }}
          />
          <CustomModal
            open={formModal}
            onClose={() => {
              setFormModal(false);
            }}
            header={`${sportsData?.id ? "Edit" : "Add"} Sports`}
            style={ModalStyle({ width: 600, height: "auto" })}
          >
            <Formik
              initialValues={sportsData}
              validationSchema={sportSchema}
              onSubmit={async (values) => {
                createSports(values);
              }}
              enableReinitialize={true}
            >
              {({
                handleSubmit,
                handleChange,
                values,
                errors,
                touched,
                setFieldValue,
              }) => (
                <>
                  <Box className="modal-body">
                    <Form onSubmit={handleSubmit}>
                      <Grid item md={12} m={2}>
                        <Grid container spacing={1} className="p-3">
                          <FormLabel className="fw-bold">
                            Sports Name:<sup className="text-danger">*</sup>
                          </FormLabel>
                          <TextField
                            className="w-100"
                            placeholder="Type here..."
                            variant="standard"
                            value={values.name}
                            name="name"
                            onChange={handleChange}
                          />
                          {touched.name && errors.name && (
                            <div className="text-danger">{errors.name}</div>
                          )}
                        </Grid>
                        {sportsData?.id && (
                          <Grid
                            container
                            spacing={1}
                            alignItems={"center"}
                            className="p-3"
                          >
                            <FormLabel
                              className="fw-bold"
                              style={{ marginRight: 8 }}
                            >
                              Status:
                            </FormLabel>
                            <Switch
                              name="status"
                              checked={values.status} // true = Active, false = InActive
                              onChange={handleChange}
                              color="primary"
                              disabled={values.id === "" ? true : false}
                            />
                          </Grid>
                        )}
                      </Grid>

                      <Box className="d-flex justify-content-end mt-3 p-3">
                        <button className="btn btn-danger" type="submit">
                          Submit
                        </button>
                      </Box>
                    </Form>
                  </Box>
                </>
              )}
            </Formik>
          </CustomModal>
        </Box>
      </AuthorizedLayout>
    </>
  );
};

export default Sports;
